// Unit picker on the nominee form.
//
// Progressive enhancement over a real <select name="nominee[unit_id]"> that is
// rendered with every unit on the order. With this pack disabled you get that
// plain dropdown and the form still submits. With it enabled the select is
// hidden and driven from the search box, which opens a panel of matching units.
//
// The first version of this filtered the <select>'s options in place. It worked
// — the option list really did narrow — but a closed native select shows
// nothing, so typing appeared to do nothing at all. Hence a panel we draw
// ourselves; the select is now storage, not UI.
const MAX_RESULTS = 50;

function initNomineeUnitSelect(root) {
  if (root.dataset.nomineeUnitReady === 'true') return;

  const search   = root.querySelector('[data-nominee-unit-search]');
  const select   = root.querySelector('[data-nominee-unit-options]');
  const panel    = root.querySelector('[data-nominee-unit-results]');
  const counter  = root.querySelector('[data-nominee-unit-count]');
  const chosen   = root.querySelector('[data-nominee-unit-selected]');
  const label    = root.querySelector('[data-nominee-unit-selected-label]');
  const clearBtn = root.querySelector('[data-nominee-unit-clear]');
  const fallback = root.querySelector('[data-nominee-unit-fallback]');

  // An order with no units renders the hand-entry fields instead — nothing here
  // to enhance.
  if (!search || !select || !panel) return;

  root.dataset.nomineeUnitReady = 'true';

  const units = Array.from(select.options)
    .filter((option) => option.value !== '')
    .map((option) => ({
      value:      option.value,
      label:      option.text,
      name:       option.dataset.name || '',
      identifier: option.dataset.identifier || '',
      address:    option.dataset.address || '',
      haystack:   (option.dataset.search || option.text || '').toLowerCase(),
    }));

  // Swap the native control for ours only now that we know we can drive it. Done
  // in JS rather than in the template so a pack that fails to load leaves a
  // usable form behind instead of a form with no unit field at all.
  if (fallback) fallback.hidden = true;
  if (chosen) chosen.hidden = false;

  let active = -1;
  let matches = [];

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

  const selectedUnit = () => units.find((unit) => unit.value === select.value);

  const renderChosen = () => {
    if (!label) return;
    const unit = selectedUnit();
    label.textContent = unit ? unit.label : 'No unit selected';
    if (clearBtn) clearBtn.hidden = !unit;
  };

  const close = () => {
    panel.hidden = true;
    panel.innerHTML = '';
    search.setAttribute('aria-expanded', 'false');
    active = -1;
    matches = [];
  };

  const setCount = (shown, filtering) => {
    if (!counter) return;
    const total = units.length;
    const noun = total === 1 ? 'unit' : 'units';
    counter.textContent = filtering
      ? `${shown} of ${total} ${noun} match`
      : `${total} ${noun} on this order`;
  };

  const choose = (unit) => {
    select.value = unit ? unit.value : '';
    // Anything else watching the field (and the browser's own form state) needs
    // to hear about a programmatic assignment; setting .value alone is silent.
    select.dispatchEvent(new Event('change', { bubbles: true }));
    search.value = '';
    renderChosen();
    setCount(units.length, false);
    close();
  };

  const paint = () => {
    Array.from(panel.children).forEach((row, i) => {
      row.classList.toggle('bg-slate-100', i === active);
      row.setAttribute('aria-selected', i === active ? 'true' : 'false');
    });
  };

  const open = () => {
    const query = search.value.trim().toLowerCase();
    const terms = query.split(/\s+/).filter(Boolean);

    matches = terms.length
      ? units.filter((unit) => terms.every((term) => unit.haystack.includes(term)))
      : units.slice();

    const shown = matches.slice(0, MAX_RESULTS);
    setCount(matches.length, terms.length > 0);

    if (shown.length === 0) {
      panel.innerHTML =
        '<li class="px-3 py-3 text-sm text-slate-400 italic">No units match that search.</li>';
      panel.hidden = false;
      search.setAttribute('aria-expanded', 'true');
      active = -1;
      return;
    }

    panel.innerHTML = shown.map((unit) => `
      <li role="option" aria-selected="false" data-value="${escapeHtml(unit.value)}"
          class="cursor-pointer px-3 py-2 hover:bg-slate-50">
        <span class="block text-sm text-slate-800">${escapeHtml(unit.name || '(no name)')}</span>
        <span class="block text-xs text-slate-500">${escapeHtml(unit.identifier)}</span>
        <span class="block text-xs text-slate-400">${escapeHtml(unit.address)}</span>
      </li>
    `).join('');

    if (matches.length > shown.length) {
      const more = document.createElement('li');
      more.className = 'px-3 py-2 text-xs text-slate-400 italic';
      more.textContent = `${matches.length - shown.length} more — keep typing to narrow.`;
      panel.appendChild(more);
    }

    Array.from(panel.querySelectorAll('li[data-value]')).forEach((row, i) => {
      // mousedown, not click: the input's blur would tear the panel down before
      // a click ever landed.
      row.addEventListener('mousedown', (event) => {
        event.preventDefault();
        choose(matches[i]);
      });
      row.addEventListener('mouseenter', () => { active = i; paint(); });
    });

    active = 0;
    panel.hidden = false;
    search.setAttribute('aria-expanded', 'true');
    paint();
  };

  search.addEventListener('input', open);
  search.addEventListener('focus', open);

  search.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (panel.hidden) { open(); return; }
      const rows = panel.querySelectorAll('li[data-value]');
      if (rows.length === 0) return;
      active = event.key === 'ArrowDown'
        ? (active + 1) % rows.length
        : (active - 1 + rows.length) % rows.length;
      paint();
      rows[active].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter') {
      // Always swallowed: with the panel open Enter picks, and with it closed a
      // stray Enter in a search box must not submit a half-filled nominee.
      event.preventDefault();
      if (!panel.hidden && active >= 0 && matches[active]) choose(matches[active]);
    } else if (event.key === 'Escape') {
      close();
    }
  });

  search.addEventListener('blur', () => { window.setTimeout(close, 120); });

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      choose(null);
      search.focus();
    });
  }

  document.addEventListener('click', (event) => {
    if (!root.contains(event.target)) close();
  });

  renderChosen();
}

function initAllNomineeUnitSelects() {
  document.querySelectorAll('[data-nominee-unit-select]').forEach(initNomineeUnitSelect);
}

// turbolinks:load covers both the first paint and every subsequent visit.
// DOMContentLoaded is the belt: if Turbolinks is ever dropped from the sprockets
// manifest the picker keeps working instead of silently reverting to a plain
// select. initNomineeUnitSelect is idempotent, so running twice is harmless.
document.addEventListener('turbolinks:load', initAllNomineeUnitSelects);
document.addEventListener('DOMContentLoaded', initAllNomineeUnitSelects);
